import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useOnSelectionChange, useReactFlow } from '@xyflow/react';
import { Button, FormControl, Grid, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import SharedDialog from '../../common/dialog/SharedDialog';
import Iconify from '../../common/iconify/Iconify';
import { LANGS_WITHOUT_TW } from '../../../Constants/lang';
import { renderConfigComponent, getNodeType } from '../nodes/registry';
import '../nodes/types';  // side-effect: registers all question types

const LANGS = LANGS_WITHOUT_TW

/**
 * PropertyPanel — right sidebar property editor.
 * Listens for node selection and renders the appropriate config form
 * from the node registry. Slides in/out with animation.
 */
export default function PropertyPanel() {

    const [open, setOpen] = useState(false)
    const [openCreateOptionDialog, setOpenCreateOptionDialog] = useState(false)
    const [createOptionData, setCreateOptionData] = useState({
        key: '',
        label: '',
        value: { EN: '', ZH_CN: '', ZH_HK: '' },
        price: '',
        inputType: '',
    })
    const [errMsg, setErrMsg] = useState('')
    const [node, setNode] = useState(null)

    const dragTimeoutRef = useRef(null)
    const { updateNode, getEdges, setEdges } = useReactFlow()

    const onChange = useCallback(({ nodes }) => {
        clearTimeout(dragTimeoutRef.current)
        setNode(nodes?.[0])
        setOpen(nodes?.length >= 1)
    }, []);

    useOnSelectionChange({ onChange });

    const nodeConfig = node ? getNodeType(node?.type) : null

    // ---- Node field handlers ----

    const handleInputChange = (e) => {
        setNode(n => ({ ...n, data: { ...n?.data, [e.target.name]: e.target.value } }))
    }

    const handleMultiInputChange = (e) => {
        if (e.target.dataset?.itemKeyName) {
            const isLangKey = LANGS.includes(e.target.name)
            setNode(n => ({
                ...n,
                data: {
                    ...n?.data,
                    [e.target.dataset?.keyName]: n?.data?.[e.target.dataset?.keyName]?.map(i =>
                        i?.key === e.target.dataset?.itemKeyName
                            ? (isLangKey
                                ? { ...i, value: { ...i?.value, [e.target.name]: e.target.value } }
                                : { ...i, [e.target.name]: e.target.value })
                            : i
                    )
                }
            }))
        } else {
            setNode(n => ({
                ...n,
                data: { ...n?.data, [e.target.dataset?.keyName]: { ...n?.data?.[e.target.dataset?.keyName], [e.target.name]: e.target.value } }
            }))
        }
    }

    const handleSwitchChange = (e, value) => {
        if (e.target.name === 'optionsNode') {
            if (value) {
                const currEdges = getEdges()
                setEdges(currEdges.filter(edge => node?.data?.id !== edge?.source))
            } else {
                const currEdges = getEdges()
                const currOptionsId = node?.data?.['answerOptionList']?.map(op => op?.key)?.filter(i => i)
                setEdges(currEdges.filter(edge => !currOptionsId?.includes(edge?.sourceHandle)))
            }
        }
        setNode(n => ({ ...n, data: { ...n?.data, [e.target.name]: value } }))
    }

    const handleSelectChange = ({ target: { value, name } }) => {
        if (name === 'type') {
            setNode(n => ({ ...n, [name]: value, data: { ...n?.data, [name]: value } }))
        } else {
            setNode(n => ({ ...n, data: { ...n?.data, [name]: value } }))
        }
    }

    const handleOptions = (type, { keyName, itemKeyName } = {}) => (e) => {
        e?.stopPropagation()
        switch (type) {
            case 'c':
                setOpenCreateOptionDialog(true)
                break;
            case 'd':
                setNode(n => ({
                    ...n,
                    data: { ...n?.data, [keyName]: n?.data?.[keyName]?.filter((op) => op?.key !== itemKeyName) }
                }))
                setEdges(e => e?.filter(i => i?.sourceHandle !== itemKeyName))
                break;
            default:
                break;
        }
    }

    const handleDragOptions = (source, destination) => {
        if (source?.droppableId === destination?.droppableId && source?.droppableId === node?.id) {
            const answerOptionList = node?.data?.answerOptionList || []
            if (answerOptionList) {
                [answerOptionList[source?.index], answerOptionList[destination?.index]] =
                    [answerOptionList[destination?.index], answerOptionList[source?.index]]
            }

            setNode(n => ({
                ...n,
                data: {
                    ...n?.data,
                    'answerOptionList': [...answerOptionList, {
                        key: '',
                        label: '',
                        value: { EN: '', ZH_CN: '', ZH_HK: '' },
                        price: '',
                        inputType: '',
                    }]
                }
            }))

            clearTimeout(dragTimeoutRef.current)
            dragTimeoutRef.current = setTimeout(() => {
                setNode(n => ({
                    ...n,
                    data: {
                        ...n?.data,
                        'answerOptionList': n?.data?.['answerOptionList']?.filter(i => i?.key !== '')
                    }
                }))
            }, 100);
        }
    }

    const handleCreateOption = () => {
        if (!createOptionData.key) {
            setErrMsg('empty key, please input again')
            return
        }
        if (node?.data?.['answerOptionList']?.map(i => i?.key).includes(createOptionData.key)) {
            setErrMsg('repeated key, please input again')
            return
        }
        setErrMsg('')
        setNode(n => ({
            ...n,
            data: {
                ...n?.data,
                'answerOptionList': n?.data?.['answerOptionList']
                    ? [...n?.data?.['answerOptionList'], createOptionData]
                    : [createOptionData]
            }
        }))
        setOpenCreateOptionDialog(false)
        setCreateOptionData(d => ({ ...d, key: '' }))
    }

    // Sync node changes back to ReactFlow
    useEffect(() => {
        if (node) updateNode(node?.id, node)
    }, [node])

    useEffect(() => {
        return () => clearTimeout(dragTimeoutRef.current)
    }, [])

    const handleClose = () => {
        setOpen(false)
        setNode(null)
    }

    return (
        <div className={`sidebar ${open ? '' : 'collapsed'}`}>
            {/* Sticky header with type info and close button */}
            <div className="sidebar-header">
                <div className="sidebar-header-title">
                    {nodeConfig && <Iconify icon={nodeConfig.icon} sx={{ width: 20, height: 20, color: nodeConfig.color }} />}
                    <span>{nodeConfig?.label || 'Properties'}</span>
                </div>
                <button className="sidebar-header-close" onClick={handleClose}>×</button>
            </div>

            <div className="sidebar-body">
                {renderConfigComponent(node?.type, {
                    data: node?.data,
                    handleIptChange: handleInputChange,
                    handleOptions,
                    handleSwitchChange,
                    handleSelectChange,
                    handleMultiIptChange: handleMultiInputChange,
                    handleDragOptions,
                })}
            </div>

            {/* Create option dialog */}
            <SharedDialog
                maxWidth='md'
                open={openCreateOptionDialog}
                onClose={() => setOpenCreateOptionDialog(false)}
                title={'Create New Option'}
                content={
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <TextField
                                variant='filled'
                                type='text'
                                label='Key'
                                name='key'
                                color='success'
                                error={!!errMsg}
                                helperText={errMsg}
                                sx={{ width: '100%' }}
                                onChange={(e) => setCreateOptionData(p => ({ ...p, [e.target.name]: e.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                variant='filled'
                                type='text'
                                label='Label'
                                name='label'
                                color='success'
                                helperText='Use for mobile'
                                sx={{ width: '100%' }}
                                value={createOptionData.label}
                                onChange={(e) => setCreateOptionData(p => ({ ...p, [e.target.name]: e.target.value }))}
                            />
                        </Grid>
                        {LANGS.map(key => (
                            <Grid key={key} item xs={4}>
                                <TextField
                                    variant='filled'
                                    type='text'
                                    label={key}
                                    name={key}
                                    color='success'
                                    sx={{ width: '100%' }}
                                    onChange={(e) => setCreateOptionData(p => ({ ...p, value: { ...p.value, [e.target.name]: e.target.value } }))}
                                />
                            </Grid>
                        ))}
                        <Grid item xs={6}>
                            <TextField
                                variant='filled'
                                type='text'
                                label='Price'
                                name='price'
                                color='success'
                                sx={{ width: '100%' }}
                                value={createOptionData.price}
                                onChange={(e) => setCreateOptionData(p => ({ ...p, [e.target.name]: e.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel id="option-input-type-label">Input Type</InputLabel>
                                <Select
                                    variant='filled'
                                    labelId="option-input-type-label"
                                    id="option-input-type"
                                    label="Input Type"
                                    value={createOptionData.inputType}
                                    color='success'
                                    name='inputType'
                                    onChange={(e) => setCreateOptionData(p => ({ ...p, [e.target.name]: e.target.value }))}
                                >
                                    <MenuItem value={''}>None</MenuItem>
                                    <MenuItem value={'TEXT_FIELD'}>TEXT_FIELD</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                }
                actions={
                    <Stack direction={'row'} gap={1}>
                        <Button variant='outlined' onClick={() => setOpenCreateOptionDialog(false)}>Cancel</Button>
                        <Button variant='contained' onClick={handleCreateOption}>Create</Button>
                    </Stack>
                }
            />
        </div>
    )
}
